import React, { KeyboardEvent } from 'react';
import './App.css';
import MaskedWordDisplay from './MaskedWordDisplay';
import HangingPerson from './HangingPerson';
import categorizedWords from './words.json'
import OnScreenKeyboard from './OnScreenKeyboard';

enum GameOver {
  WON,
  LOST,
};

function chooseRandomWord(): [string, string] {
  const allWords = Object.entries(categorizedWords)
    .flatMap(([category, list]) => list.map((word) => [word, category] as [string, string]))
  return allWords[Math.floor(Math.random() * allWords.length)]
}

function isLetter(key: string): boolean {
  return key.match(/^[a-z]$/i) != null
}

function App() {
  const [guessedLetters, setGuessedLetters] = React.useState<Array<string>>([]);
  const [gameOver, setGameOver] = React.useState<GameOver | null>(null);
  const [[word, category], setWord] = React.useState<[string, string]>(chooseRandomWord());
  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleGuess = React.useCallback((guessedLetter: string) => {
    if (!guessedLetters.includes(guessedLetter)) {
      setGuessedLetters([...guessedLetters, guessedLetter]);
    }
  }, [guessedLetters]);

  const handleKeyDown = React.useCallback((e: KeyboardEvent<HTMLInputElement>) => {
    e.preventDefault();

    if (gameOver || e.metaKey || e.ctrlKey || e.altKey) {
      return;
    }

    if (isLetter(e.key)) {
      const guessedLetter = e.key.toLowerCase();
      handleGuess(guessedLetter);
    }

    return false;
  }, [handleGuess, gameOver]);

  const handleWordGuessed = React.useCallback(() => {
    setGameOver(GameOver.WON);
  }, [setGameOver]);

  const handlePersonHung = React.useCallback(() => {
    setGameOver(GameOver.LOST);
  }, [setGameOver]);

  const handleClickPlayAgain = React.useCallback((e: any) => {
    e.preventDefault();
    setGuessedLetters([]);
    setGameOver(null);
    setWord(chooseRandomWord());
    inputRef.current!.focus();
  }, [setGameOver]);

  return (
    <div className="App">
      <div className="panel">
        <p className="category">Category: {category}</p>
        <p className="word"><MaskedWordDisplay word={word} guessedLetters={guessedLetters} onWordGuessed={handleWordGuessed} /></p>
        <OnScreenKeyboard guessedLetters={guessedLetters} disabled={gameOver !== null} onGuess={handleGuess} />
        <input type="text" ref={inputRef} className="letter-input" maxLength={1} onKeyDown={handleKeyDown} />

        {
          gameOver !== null && (
            <div>
              <h1 className="game-over">
                {gameOver === GameOver.WON && "You won!"}
                {gameOver === GameOver.LOST && "Try again!"}
              </h1>
              <button type="button" onClick={handleClickPlayAgain}>Play again</button>
            </div>
          )
        }
      </div>

      <div className="panel">
        <p>Incorrect guesses: {guessedLetters.filter((letter) => !word.includes(letter)).join(', ')}</p>
        <HangingPerson word={word} guessedLetters={guessedLetters} onPersonHung={handlePersonHung} />
      </div>
    </div>
  );
}

export default App;
